import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Save, Building2, CreditCard, Globe2, Clock4, Sparkles, Zap, CheckCircle2, Loader2, AlertCircle, Search, X, Stethoscope, Megaphone, ShieldCheck, Settings as SettingsIcon, Plug, ListFilter, ClipboardCheck, Send } from 'lucide-react';
import PlatformClinicsCard from './PlatformClinicsCard';
import ClinicLocationsCard from './ClinicLocationsCard';
import EmailDeliverabilityCard from './EmailDeliverabilityCard';
import { WORLD_CURRENCIES } from '../../lib/currencies';
import { DISCIPLINES } from '../../lib/disciplines';
import { safeConfirm } from '../../lib/confirm';

const AUTO_SAVE_DEBOUNCE_MS = 1500;

// Iter 66 — Category map drives the sticky tabs filter.
// `data-cat` attributes on cards must match one of these keys.
// Simple view — the essential cards a new/small clinic actually needs.
// Everything else (AI, compliance detail, integrations, advanced ops) hides
// until the admin switches back to "All settings".
const SIMPLE_VIEW_CARDS = new Set([
  'clinic-details-card',
  'business-hours-card',
  'scheduling-time-card',
  'payments-card',
  'service-disciplines-card',
  'feature-flags-card',
]);

const SETTINGS_CATEGORIES = [
  { id: 'all',         label: 'All',                       icon: ListFilter },
  { id: 'clinical',    label: 'Clinical',                  icon: Stethoscope },
  { id: 'finance',     label: 'Finance & Memberships',     icon: CreditCard },
  { id: 'marketing',   label: 'Marketing & Reviews',       icon: Megaphone },
  { id: 'compliance',  label: 'Compliance & Security',     icon: ShieldCheck },
  { id: 'operations',  label: 'Operations',                icon: SettingsIcon },
  { id: 'ai',          label: 'AI features',               icon: Sparkles },
  { id: 'integrations',label: 'Integrations',              icon: Plug },
];

export default function AdminSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [serverSnapshot, setServerSnapshot] = useState(null); // last-saved state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);
  // iter 66 — search + category filter for the long list of settings cards.
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [simpleView, setSimpleView] = useState(() => window.localStorage.getItem('settings:simple-view') === '1');
  const rootRef = useRef(null);

  const toggleSimpleView = () => {
    setSimpleView(v => {
      window.localStorage.setItem('settings:simple-view', v ? '0' : '1');
      return !v;
    });
  };

  useEffect(() => {
    api.get('/clinic/settings').then(r => {
      setS(r.data);
      setServerSnapshot(JSON.stringify(r.data));
    });
  }, []);

  // Compare current state vs last-saved snapshot to know if there are unsaved changes.
  const isDirty = s && serverSnapshot && JSON.stringify(s) !== serverSnapshot;

  // FIX iter 65: previously this used `setS({...s, [k]: v})` which captures a stale
  // `s` from the closure — fast successive toggle clicks would wipe each other.
  // Switching to the functional setter resolves the "toggle stays off / flips back" bug
  // users reported on the settings page.
  const up = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const toggleCurrency = (c) => {
    setS(prev => {
      const cur = new Set(prev.supported_currencies || []);
      if (cur.has(c)) cur.delete(c); else cur.add(c);
      return { ...prev, supported_currencies: Array.from(cur) };
    });
  };

  const save = async () => {
    if (!s) return;
    setSaving(true);
    setError('');
    try {
      const payload = { ...s, prepay_percent: parseFloat(s.prepay_percent) || 0 };
      const { data } = await api.put('/clinic/settings', payload);
      // Update snapshot with server-returned doc so subsequent changes are compared correctly.
      setServerSnapshot(JSON.stringify(data || payload));
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed — your changes are still in this form.');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save on idle. Debounced so a quick burst of edits only fires one PUT.
  useEffect(() => {
    if (!isDirty || saving) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { save(); }, AUTO_SAVE_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s, isDirty]);

  // Warn the user if they try to leave the page with unsaved changes.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (isDirty || saving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty, saving]);

  // Filter cards by data-cat + free-text search. We walk the rendered DOM so
  // we don't have to refactor 15+ admin-card definitions.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const q = search.trim().toLowerCase();
    const cards = root.querySelectorAll('.admin-card');
    let visibleCount = 0;
    cards.forEach(card => {
      const cat = card.dataset.cat || 'operations';
      const text = (card.textContent || '').toLowerCase();
      const matchCat = category === 'all' || cat === category;
      const matchQ = !q || text.includes(q);
      const matchSimple = !simpleView || SIMPLE_VIEW_CARDS.has(card.dataset.testid);
      const show = matchCat && matchQ && matchSimple;
      card.style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });
    const empty = root.querySelector('[data-settings-empty]');
    if (empty) empty.style.display = visibleCount === 0 ? '' : 'none';
  }, [search, category, s, simpleView]);

  // Iter 91 — Persist each collapsible card's open/closed state in localStorage
  // (keyed by data-testid) so the user's preferred layout survives reload.
  // Also wire up the Expand-all / Collapse-all buttons.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const allDetails = root.querySelectorAll('details.admin-card[data-testid]');
    const listeners = [];
    allDetails.forEach(d => {
      const key = `settings:open:${d.dataset.testid}`;
      const stored = window.localStorage.getItem(key);
      if (stored === '1') d.setAttribute('open', '');
      else if (stored === '0') d.removeAttribute('open');
      const onToggle = () => {
        window.localStorage.setItem(key, d.open ? '1' : '0');
      };
      d.addEventListener('toggle', onToggle);
      listeners.push([d, onToggle]);
    });
    return () => listeners.forEach(([d, fn]) => d.removeEventListener('toggle', fn));
  }, [s]);

  const setAllOpen = (open) => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll('details.admin-card[data-testid]').forEach(d => {
      if (open) d.setAttribute('open', '');
      else d.removeAttribute('open');
      window.localStorage.setItem(`settings:open:${d.dataset.testid}`, open ? '1' : '0');
    });
  };

  if (!s) return <div className="p-8">Loading…</div>;

  // Compact save-state indicator used in the sticky footer + header.
  const StateIndicator = () => {
    if (saving) return <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted" data-testid="save-state-saving"><Loader2 className="w-3.5 h-3.5 animate-spin"/> Saving…</span>;
    if (error)  return <span className="inline-flex items-center gap-1.5 text-xs text-[#B83A3A]" data-testid="save-state-error"><AlertCircle className="w-3.5 h-3.5"/> {error}</span>;
    if (saved)  return <span className="inline-flex items-center gap-1.5 text-xs text-[#1B5E20]" data-testid="save-state-saved"><CheckCircle2 className="w-3.5 h-3.5"/> Saved</span>;
    if (isDirty) return <span className="inline-flex items-center gap-1.5 text-xs text-[#B8860B]" data-testid="save-state-dirty"><Clock4 className="w-3.5 h-3.5"/> Unsaved · auto-save in {Math.round(AUTO_SAVE_DEBOUNCE_MS/1000)}s</span>;
    return <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted" data-testid="save-state-clean"><CheckCircle2 className="w-3.5 h-3.5"/> All changes saved</span>;
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl pb-28">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CLINIC</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Settings</h1>
          <div className="mt-2"><StateIndicator/></div>
        </div>
        <button onClick={save} disabled={saving || !isDirty} className={`btn-primary inline-flex items-center gap-2 ${!isDirty ? 'opacity-60' : ''}`} data-testid="save-settings">
          <Save className="w-4 h-4"/> {saving ? 'Saving…' : (isDirty ? 'Save now' : 'Saved')}
        </button>
      </div>

      {/* iter 66 — search + category quick-filter. The DOM-walking effect above
          hides any .admin-card whose data-cat doesn't match. */}
      <div className="surface border border-soft rounded-lg p-3 mb-5 sticky top-2 z-20 shadow-sm" data-testid="settings-filter-bar">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"/>
            <input
              type="text"
              className="input pl-9 pr-9"
              placeholder="Search settings (e.g. 'stripe', 'gdpr', 'no-show')…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="settings-search"
            />
            {search && (
              <button onClick={()=>setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg" data-testid="settings-search-clear">
                <X className="w-4 h-4"/>
              </button>
            )}
          </div>
          <div className="text-xs text-fg-muted flex items-center gap-3">
            <button onClick={toggleSimpleView}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition font-medium ${simpleView ? 'bg-brand text-white' : 'bg-bg-secondary/40 text-fg-secondary hover:bg-bg-secondary'}`}
                    title="Show only the essential settings — hide advanced cards you rarely touch"
                    data-testid="settings-simple-toggle">
              {simpleView ? '✓ Simple view — show all' : 'Simple view'}
            </button>
            {category !== 'all' && <button onClick={()=>setCategory('all')} className="underline hover:text-fg">Clear filter</button>}
            <span className="text-fg-muted/40">|</span>
            <button onClick={()=>setAllOpen(true)} className="underline hover:text-fg" data-testid="settings-expand-all">Expand all</button>
            <button onClick={()=>setAllOpen(false)} className="underline hover:text-fg" data-testid="settings-collapse-all">Collapse all</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="settings-categories">
          {SETTINGS_CATEGORIES.map(c => {
            const Icon = c.icon;
            const active = category === c.id;
            return (
              <button key={c.id} onClick={()=>setCategory(c.id)}
                      data-testid={`settings-cat-${c.id}`}
                      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
                        active ? 'bg-brand text-white' : 'bg-bg-secondary/40 text-fg-secondary hover:bg-bg-secondary'
                      }`}>
                <Icon className="w-3.5 h-3.5"/>
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {simpleView && (
        <div className="surface border border-brand/30 bg-brand/5 rounded-lg px-4 py-2.5 mb-4 text-xs text-fg-secondary flex items-center gap-2" data-testid="settings-simple-banner">
          <Sparkles className="w-3.5 h-3.5 text-brand shrink-0"/>
          Simple view is on — only the essentials are shown (clinic details, hours, scheduling, payments, services, feature flags). Advanced cards are hidden, not disabled.
          <button onClick={toggleSimpleView} className="underline hover:text-fg ml-auto whitespace-nowrap">Show all settings</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4" ref={rootRef}>
        <div data-settings-empty className="md:col-span-2 surface border border-soft rounded-lg p-10 text-center text-sm text-fg-muted" style={{ display: 'none' }}>
          No settings match — try a different keyword or clear the filter.
        </div>
        <details className="admin-card md:col-span-2" data-cat="operations" data-testid="clinic-details-card" open>
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Clinic details</h3>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <div className="grid md:grid-cols-2 gap-x-4 mt-3">
            <Field label="Clinic name"><input className="input" value={s.clinic_name||''} onChange={e=>up('clinic_name', e.target.value)} data-testid="s-clinic-name"/></Field>
            <Field label="Tagline"><input className="input" value={s.clinic_tagline||''} onChange={e=>up('clinic_tagline', e.target.value)} placeholder="Recover · Rehab · Train · Prevent" data-testid="s-tagline"/></Field>
            <Field label="Phone"><input className="input" value={s.phone||''} onChange={e=>up('phone', e.target.value)} data-testid="s-phone"/></Field>
            <Field label="Email"><input className="input" value={s.email||''} onChange={e=>up('email', e.target.value)} data-testid="s-email"/></Field>
            <Field label="Address"><input className="input" value={s.address||''} onChange={e=>up('address', e.target.value)}/></Field>
            <Field label="Website"><input className="input" value={s.website||''} onChange={e=>up('website', e.target.value)}/></Field>
          </div>
          <div className="mt-3 pt-3 border-t border-soft grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs text-fg-muted mb-2">Clinic logo <span className="text-fg-muted/70">(used in sidebar, invoices, patient portal)</span></div>
              <div className="flex items-center gap-3">
                {s.clinic_logo_url ? (
                  <img src={s.clinic_logo_url} alt="logo" className="w-16 h-16 rounded-lg object-cover border border-soft" data-testid="logo-preview"/>
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-[#F2EBDB] border border-soft"/>
                )}
                <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1" data-testid="logo-upload-btn">
                  Upload logo
                  <input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input"
                         onChange={(e)=>{const f=e.target.files?.[0]; if(!f) return; if(f.size>1024*1024*1) { alert('Keep logo under 1 MB.'); return;} const r=new FileReader(); r.onload=(ev)=>up('clinic_logo_url', ev.target.result); r.readAsDataURL(f);}}/>
                </label>
                {s.clinic_logo_url && <button onClick={()=>up('clinic_logo_url', null)} className="text-xs text-[#B83A3A]" data-testid="logo-clear">Remove</button>}
              </div>
            </div>
            <div>
              <div className="text-xs text-fg-muted mb-2">Favicon <span className="text-fg-muted/70">(browser tab icon — 32x32 recommended)</span></div>
              <div className="flex items-center gap-3">
                {s.clinic_favicon_url ? (
                  <img src={s.clinic_favicon_url} alt="favicon" className="w-12 h-12 rounded-md object-contain border border-soft p-1 bg-white" data-testid="favicon-preview"/>
                ) : (
                  <img src="/favicon.svg" alt="default favicon" className="w-12 h-12 rounded-md object-contain border border-soft p-1 bg-white opacity-60" data-testid="favicon-default"/>
                )}
                <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1" data-testid="favicon-upload-btn">
                  Upload favicon
                  <input type="file" accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon" className="hidden" data-testid="favicon-upload-input"
                         onChange={(e)=>{const f=e.target.files?.[0]; if(!f) return; if(f.size>200*1024) { alert('Keep favicon under 200 KB.'); return;} const r=new FileReader(); r.onload=(ev)=>up('clinic_favicon_url', ev.target.result); r.readAsDataURL(f);}}/>
                </label>
                {s.clinic_favicon_url && <button onClick={()=>up('clinic_favicon_url', null)} className="text-xs text-[#B83A3A]" data-testid="favicon-clear">Remove</button>}
              </div>
              <div className="text-[10px] text-fg-muted mt-1.5">Applies on next page load. PNG, SVG, or ICO.</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-soft">
            <div className="text-xs text-fg-muted mb-2">Theme preset (applied site-wide)</div>
            <div className="grid grid-cols-3 gap-2" data-testid="theme-presets">
              {[
                { key: 'default',  label: 'Logo Blue',       sw: ['#003D80', '#FFB000', '#FAF6EE'] },
                { key: 'warm',     label: 'Warm Earth',      sw: ['#2D4A43', '#C45B46', '#F8F7F4'] },
                { key: 'clinical', label: 'Clinical Neutral', sw: ['#1F2937', '#2563EB', '#F7F8FA'] },
              ].map(p => {
                const active = (s.theme_variant || 'default') === p.key;
                return (
                  <button key={p.key} type="button" onClick={() => {
                      up('theme_variant', p.key);
                      if (p.key === 'default') document.documentElement.removeAttribute('data-theme');
                      else document.documentElement.setAttribute('data-theme', p.key);
                    }}
                    className={`p-2.5 rounded-lg border text-left transition ${active ? 'border-brand ring-2 ring-brand/20 bg-brand/5' : 'border-soft hover:border-fg/30'}`}
                    data-testid={`theme-${p.key}`}>
                    <div className="flex gap-1 mb-1.5">
                      {p.sw.map((c, i) => <span key={i} className="w-4 h-4 rounded-sm border border-black/10" style={{ background: c }}/>)}
                    </div>
                    <div className="text-xs font-medium">{p.label}</div>
                    {active && <div className="text-[10px] text-brand mt-0.5">Active</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-soft">
            <div className="text-xs text-fg-muted mb-2">Brand color overrides (optional — leaves theme preset untouched if blank)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primary color">
                <div className="flex items-center gap-2">
                  <input type="color" value={s.brand_primary_color || '#003D80'}
                         onChange={e=>up('brand_primary_color', e.target.value)}
                         className="w-10 h-9 rounded border border-soft cursor-pointer"
                         data-testid="s-brand-primary"/>
                  <input className="input flex-1 text-xs" value={s.brand_primary_color||''}
                         onChange={e=>up('brand_primary_color', e.target.value)}
                         placeholder="#003D80 (leave blank for theme default)"/>
                  {s.brand_primary_color && (
                    <button onClick={()=>up('brand_primary_color', null)} className="text-xs text-[#B83A3A]" data-testid="s-brand-primary-clear">Clear</button>
                  )}
                </div>
              </Field>
              <Field label="Accent color">
                <div className="flex items-center gap-2">
                  <input type="color" value={s.brand_accent_color || '#FFB000'}
                         onChange={e=>up('brand_accent_color', e.target.value)}
                         className="w-10 h-9 rounded border border-soft cursor-pointer"
                         data-testid="s-brand-accent"/>
                  <input className="input flex-1 text-xs" value={s.brand_accent_color||''}
                         onChange={e=>up('brand_accent_color', e.target.value)}
                         placeholder="#FFB000 (leave blank for theme default)"/>
                  {s.brand_accent_color && (
                    <button onClick={()=>up('brand_accent_color', null)} className="text-xs text-[#B83A3A]" data-testid="s-brand-accent-clear">Clear</button>
                  )}
                </div>
              </Field>
            </div>
            <div className="text-[10px] text-fg-muted mt-1.5">
              Changes apply immediately after Save + page refresh. Use this to align Physiogenic to a partner clinic's brand.
            </div>
            {/* Iter 121 — hide the "Powered by Physiogenic" footer on the
                public /c/:slug page. Off by default; paid subscribers can
                flip it on for a fully white-labelled public URL. */}
            <label className="flex items-start gap-3 mt-4 p-3 border border-soft rounded-lg cursor-pointer hover:bg-[#FAF6EE]/40 transition">
              <input
                type="checkbox"
                checked={!!s.hide_powered_by}
                onChange={e=>up('hide_powered_by', e.target.checked)}
                className="mt-0.5 w-4 h-4 cursor-pointer"
                data-testid="s-hide-powered-by"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold">Hide "Powered by Physiogenic" on my public booking page</div>
                <div className="text-[11px] text-fg-muted mt-0.5">
                  When enabled, the footer on your public /c/{s.clinic_slug || 'your-clinic'} page won't mention Physiogenic — a fully white-labelled experience for your patients.
                </div>
              </div>
            </label>
          </div>
        </details>

        {(s.feature_payments_module ?? true) && (
        <details className="admin-card md:col-span-2" data-cat="finance" data-testid="payments-card">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Payments</h3>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <p className="text-xs text-fg-muted mt-3 mb-4">
            Prepayment deposits, supported currencies, and your Stripe Connect account — all in one place.
          </p>

          {/* ── Section: Prepayment & currencies ──────────────────────── */}
          <div className="text-xs uppercase tracking-wider text-fg-muted mb-3 font-medium">Deposits & currency</div>
          <div className="flex items-center gap-3 mb-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!s.prepay_enabled} onChange={e=>up('prepay_enabled', e.target.checked)} data-testid="s-prepay-enabled"/>
              <span className="text-sm">Require patients to prepay a deposit when booking</span>
            </label>
          </div>
          <div className="flex flex-col gap-2 mb-3">
            <label className={`inline-flex items-center gap-2 cursor-pointer ${!s.prepay_enabled ? 'opacity-50' : ''}`}>
              <input type="checkbox" checked={!!s.prepay_electronic_only} disabled={!s.prepay_enabled}
                     onChange={e=>up('prepay_electronic_only', e.target.checked)} data-testid="s-prepay-electronic-only"/>
              <span className="text-sm">Electronic prepayment only — <strong>BLIK or card (Stripe)</strong>, no cash-on-arrival</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={s.auto_refund_card !== false}
                     onChange={e=>up('auto_refund_card', e.target.checked)} data-testid="s-auto-refund-card"/>
              <span className="text-sm">Automatically refund <strong>card</strong> prepayments on eligible cancellations (outside the window below)</span>
            </label>
            <div className="text-[11px] text-fg-muted ml-6 -mt-1">
              Card refunds go back via Stripe instantly. BLIK/cash refunds stay manual (Payments → mark refunded).
              The refund window is the <strong>Cancellation window</strong> in the Scheduling section.
            </div>
            <label className="flex items-center gap-2 mt-1">
              <span className="text-sm">Release unpaid self-booked slots after</span>
              <input type="number" min="0" max="1440" step="5" className="input !w-20 !py-1"
                     value={s.unpaid_release_minutes ?? 30}
                     onChange={e=>up('unpaid_release_minutes', Math.max(0, parseInt(e.target.value || 0, 10)))}
                     data-testid="s-unpaid-release-min"/>
              <span className="text-sm">minutes <span className="text-fg-muted text-xs">(0 = never; freed slots go to the waitlist automatically)</span></span>
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Prepay percent (%)"><input type="number" min="0" max="100" step="5" className="input w-full" value={s.prepay_percent||0} onChange={e=>up('prepay_percent', e.target.value)} disabled={!s.prepay_enabled} data-testid="s-prepay-pct"/></Field>
            <Field label="Default currency">
              <select className="input w-full" value={s.default_currency||'PLN'} onChange={e=>up('default_currency', e.target.value)}>
                {(s.supported_currencies||[]).map(c=><option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-2 flex items-center gap-1"><Globe2 className="w-3.5 h-3.5"/> Supported currencies</div>
            <div className="flex flex-wrap gap-2 mb-2">
              {(s.supported_currencies || []).map(c => (
                <span key={c}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border bg-brand text-white border-brand"
                      data-testid={`toggle-cur-${c}`}>
                  {c}
                  <button type="button" onClick={()=>toggleCurrency(c)} aria-label={`Remove ${c}`}
                          className="hover:opacity-70" data-testid={`remove-cur-${c}`}>
                    <X className="w-3 h-3"/>
                  </button>
                </span>
              ))}
            </div>
            <CurrencyAdder selected={s.supported_currencies || []} onAdd={toggleCurrency}/>
          </div>

          <div className="mt-5 pt-4 border-t border-soft">
            <div className="text-xs uppercase tracking-wider text-fg-muted mb-2">Patient payment methods</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { k: 'card', label: 'Card (Visa · Mastercard)' },
                { k: 'blik', label: 'BLIK' },
                { k: 'cash', label: 'Cash' },
              ].map(({ k: m, label }) => {
                const on = !!(s.payment_methods && s.payment_methods[m]);
                return (
                  <button key={m} type="button"
                          onClick={()=>up('payment_methods', { ...(s.payment_methods || {}), [m]: !on })}
                          className={`px-3 py-1.5 text-sm rounded-md border ${on ? 'bg-brand text-white border-brand' : 'border-soft text-fg-muted'}`}
                          data-testid={`toggle-method-${m}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            {!!(s.payment_methods && s.payment_methods.card) && (
              <div className="text-[11px] text-fg-muted mb-3 flex items-start gap-1.5" data-testid="card-method-hint">
                <CreditCard className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand"/>
                <span>
                  Card deposits are collected via <strong>Stripe Checkout</strong> (Visa, Mastercard, Amex —
                  plus BLIK/P24 when enabled in the Stripe Connect section below). Payments land in your
                  connected Stripe account; until you connect one, the platform account is used.
                </span>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="BLIK phone number">
                <input className="input" value={s.blik_phone||''} onChange={e=>up('blik_phone', e.target.value)} data-testid="s-blik" placeholder="+48 793 513 208"/>
              </Field>
            </div>
            <details className="mt-2">
              <summary className="text-xs text-fg-muted cursor-pointer">Customize payment instructions (optional)</summary>
              <div className="grid gap-3 mt-3">
                <Field label="BLIK note (placeholders: {phone}, {amount}, {currency})">
                  <input className="input" value={s.payment_notes_blik||''} onChange={e=>up('payment_notes_blik', e.target.value)}/>
                </Field>
                <Field label="Cash note">
                  <input className="input" value={s.payment_notes_cash||''} onChange={e=>up('payment_notes_cash', e.target.value)}/>
                </Field>
              </div>
            </details>
          </div>

          {/* ── Section: Stripe Connect ───────────────────────────────── */}
          <div className="mt-6 pt-5 border-t border-soft">
            <StripeConnectSection/>
          </div>
        </details>
        )}

        {/* Iter 136 — Scheduling & time: clinic timezone, 12/24h clock,
            appointment buffer, cancellation window (24h platform minimum). */}
        <details className="admin-card md:col-span-2" data-cat="operations" data-testid="scheduling-time-card">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <Clock4 className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Scheduling & time</h3>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <p className="text-xs text-fg-muted mt-3 mb-4">
            All appointment and slot times are shown in the clinic's timezone — on every device.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Clinic timezone">
              <select className="input w-full" value={s.timezone || 'Europe/Warsaw'}
                      onChange={e=>up('timezone', e.target.value)} data-testid="s-timezone">
                {['Europe/Warsaw','Europe/London','Europe/Berlin','Europe/Paris','Europe/Madrid','Europe/Rome',
                  'Europe/Amsterdam','Europe/Brussels','Europe/Vienna','Europe/Prague','Europe/Lisbon','Europe/Dublin',
                  'Europe/Stockholm','Europe/Oslo','Europe/Copenhagen','Europe/Helsinki','Europe/Athens','Europe/Bucharest',
                  'Europe/Kyiv','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Toronto',
                  'America/Vancouver','America/Mexico_City','America/Sao_Paulo','America/Puerto_Rico','Asia/Dubai','Asia/Kolkata',
                  'Asia/Singapore','Asia/Tokyo','Asia/Hong_Kong','Australia/Sydney','Pacific/Auckland','Africa/Johannesburg','UTC',
                ].map(tz => <option key={tz} value={tz}>{tz.replace('_',' ')}</option>)}
              </select>
            </Field>
            <Field label="Clock format">
              <div className="flex gap-2">
                {[['24h','24-hour (14:30)'],['12h','12-hour (2:30 PM)']].map(([k,label]) => (
                  <button key={k} type="button" onClick={()=>up('clock_format', k)}
                          className={`px-3 py-2 text-sm rounded-md border flex-1 ${(s.clock_format||'24h')===k ? 'bg-brand text-white border-brand' : 'border-soft text-fg-muted'}`}
                          data-testid={`clock-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Buffer between appointments (minutes)">
              <input type="number" min="0" max="120" step="5" className="input w-full"
                     value={s.default_buffer_min ?? 0}
                     onChange={e=>up('default_buffer_min', parseInt(e.target.value || 0, 10))}
                     data-testid="s-buffer-min"/>
              <div className="text-[11px] text-fg-muted mt-1">
                Clinic-wide default — per-clinician overrides live under <strong>Clinician hours</strong>.
              </div>
            </Field>
            <Field label="Cancellation window (hours before start)">
              <input type="number" min="24" max="168" className="input w-full"
                     value={s.cancellation_hours ?? 24}
                     onChange={e=>up('cancellation_hours', Math.max(24, parseInt(e.target.value || 24, 10)))}
                     data-testid="s-cancellation-hours"/>
              <div className="text-[11px] text-fg-muted mt-1">
                Platform minimum is <strong>24 hours</strong>. Cancelling <strong>outside</strong> this window refunds the prepay
                (card refunds are automatic when enabled in Payments); cancelling inside it forfeits the prepay.
              </div>
            </Field>
          </div>
        </details>

        <details className="admin-card md:col-span-2" data-testid="business-hours-card" data-cat="operations">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <Clock4 className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold inline-flex items-center gap-2">Business hours
                <span className="text-[10px] uppercase tracking-wider bg-brand/10 text-brand px-1.5 py-0.5 rounded font-medium" data-testid="bh-summary">
                  {(() => {
                    const days = ['mon','tue','wed','thu','fri','sat','sun'];
                    const open = days.filter(d => (s.business_hours && s.business_hours[d] && s.business_hours[d].active));
                    return `${open.length} of 7 days open`;
                  })()}
                </span>
              </h3>
            </div>
            <span className="text-xs text-fg-muted">Click to expand ▾</span>
          </summary>
          <p className="text-xs text-fg-muted mt-3 mb-3">
            Used on the landing page footer + invoice PDFs. Per-clinician hours (overrides used for booking)
            live in <strong>Admin → Clinicians → Hours</strong>.
          </p>
          <div className="grid gap-2" data-testid="business-hours">
            {['mon','tue','wed','thu','fri','sat','sun'].map(d => {
              const h = (s.business_hours && s.business_hours[d]) || { open: '09:00', close: '18:00', active: false };
              const setH = (patch) => up('business_hours', { ...(s.business_hours || {}), [d]: { ...h, ...patch } });
              const labels = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
              return (
                <div key={d} className="flex items-center gap-3 flex-wrap" data-testid={`bh-${d}`}>
                  <label className="inline-flex items-center gap-2 min-w-[160px] cursor-pointer">
                    <input type="checkbox" checked={!!h.active}
                           onChange={e=>setH({ active: e.target.checked })}
                           data-testid={`bh-${d}-active`}/>
                    <span className="text-sm">{labels[d]}</span>
                  </label>
                  <input type="time" className="input w-40" value={h.open || '09:00'}
                         onChange={e=>setH({ open: e.target.value })}
                         disabled={!h.active} data-testid={`bh-${d}-open`}/>
                  <span className="text-xs text-fg-muted">to</span>
                  <input type="time" className="input w-40" value={h.close || '18:00'}
                         onChange={e=>setH({ close: e.target.value })}
                         disabled={!h.active} data-testid={`bh-${d}-close`}/>
                  {!h.active && <span className="text-[11px] text-fg-muted italic">Closed</span>}
                </div>
              );
            })}
          </div>
        </details>

        <div className="admin-card md:col-span-2" data-testid="clinic-mode-card" data-cat="operations">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Clinic mode</h3>
          </div>
          <p className="text-xs text-fg-muted mb-4">
            Switch between a single-practitioner setup and a multi-location team. This is a single mode —
            <strong> you're either Solo or Multi</strong> — but you can flip it any time as your practice grows.
            <span className="block mt-1 text-[11px] italic">No data is lost when switching: it only changes sidebar menus, invoicing defaults, and reporting granularity.</span>
          </p>

          <div className="flex items-center gap-3 surface border border-soft rounded-lg p-4 bg-[#FAF6EE]/40" key={`mode-${!!s.feature_multi_location}`}>
            <button
              type="button"
              onClick={() => up('feature_multi_location', !s.feature_multi_location)}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${s.feature_multi_location ? 'bg-brand' : 'bg-fg-muted/30'}`}
              role="switch"
              aria-checked={!!s.feature_multi_location}
              data-testid="clinic-mode-toggle">
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${s.feature_multi_location ? 'translate-x-6' : 'translate-x-1'}`}/>
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold inline-flex items-center gap-2">
                {s.feature_multi_location
                  ? <><Building2 className="w-4 h-4 text-brand"/> Multi-clinic / multi-location</>
                  : <><Sparkles  className="w-4 h-4 text-brand"/> Solo clinic</>}
                <span className="text-[10px] uppercase tracking-wider bg-brand text-white px-1.5 py-0.5 rounded" data-testid={`clinic-mode-active-${s.feature_multi_location ? 'multi' : 'solo'}`}>Active</span>
              </div>
              <p className="text-xs text-fg-muted mt-0.5">
                {s.feature_multi_location
                  ? 'Multiple practitioners, multiple branches, per-location reporting.'
                  : 'One practitioner, one location, simplified menus.'}
              </p>
            </div>
          </div>

          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-fg-muted hover:text-brand inline-flex items-center gap-1">
              When should I pick which?
            </summary>
            <div className="mt-2 grid sm:grid-cols-2 gap-3">
              <div className="surface border border-soft rounded p-3" data-testid="clinic-mode-solo-explainer">
                <div className="font-heading font-semibold text-fg flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-brand"/> Pick Solo if…</div>
                <ul className="text-fg-muted list-disc list-inside mt-1.5 space-y-0.5">
                  <li>You work alone or with at most one assistant</li>
                  <li>You operate from one physical location</li>
                  <li>You want the simplest sidebar / fewest features</li>
                </ul>
              </div>
              <div className="surface border border-soft rounded p-3" data-testid="clinic-mode-multi-explainer">
                <div className="font-heading font-semibold text-fg flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-brand"/> Pick Multi if…</div>
                <ul className="text-fg-muted list-disc list-inside mt-1.5 space-y-0.5">
                  <li>You have 2+ clinicians on your team</li>
                  <li>You have multiple physical branches</li>
                  <li>You want per-location P&amp;L and KPIs</li>
                  <li>You're hiring receptionists with their own logins</li>
                </ul>
              </div>
            </div>
          </details>
        </div>

        <details className="admin-card md:col-span-2" data-cat="ai" data-testid="feature-flags-card">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Feature flags</h3>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <p className="text-xs text-fg-muted mt-3 mb-3">Enable / disable major surfaces without redeploying.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="feature-flags">
            <Toggle k="feature_objective_examination" v={s} on={up} label="Objective examination" testid="ff-obj-exam"/>
            <Toggle k="feature_payments_module" v={s} on={up} label="Payments (invoices + Stripe)" testid="ff-payments"/>
            <Toggle k="feature_clinical_detail" v={s} on={up} label="Clinical detail (interview, charts)" testid="ff-clinical"/>
            <Toggle k="feature_review_thank_you" v={s} on={up} label="Auto thank-you for reviews" testid="ff-thankyou"/>
            <Toggle k="feature_ai_scribe" v={s} on={up} label="AI medical scribe (ambient voice)" testid="ff-ai-scribe"/>
            {/* Iter 131 — Fitness is a paid add-on. Show the current activation
                state (from clinic_settings.feature_fitness) as a read-only chip
                with a CTA to the purchase / request page. Only platform_admin
                keeps the write-toggle (for manual comp'ed activations). */}
            {user?.role === 'platform_admin' ? (
              <Toggle k="feature_fitness" v={s} on={up} label="Fitness & wellness (platform-admin override)" testid="ff-fitness"/>
            ) : (
              <AddonRow
                feature={s.feature_fitness}
                to="/admin/fitness-addon"
                label="Fitness & wellness"
                testid="ff-fitness-addon-cta"
              />
            )}
            <Toggle k="feature_fitness_in_catalogue" v={s} on={up} label="Show Fitness in product catalogue" testid="ff-fitness-catalogue"/>
            <Toggle k="feature_require_complete_profile" v={s} on={up} label="Require complete profile before booking" testid="ff-require-profile"/>
            <Toggle k="feature_hr" v={s} on={up} label="HR — hiring & staff records" testid="ff-hr"/>
            <Toggle k="feature_ops_manual" v={s} on={up} label="Daily Operations Manual (staff visibility)" testid="ff-ops-manual"/>
            <Toggle k="feature_setup_manual" v={s} on={up} label="Clinic Setup Manual (staff visibility)" testid="ff-setup-manual"/>
            <Toggle k="feature_telehealth" v={s} on={up} label="Telehealth (Jitsi video calls)" testid="ff-telehealth"/>
            <Toggle k="feature_waitlist" v={s} on={up} label="Waitlist auto-notify" testid="ff-waitlist"/>
            <Toggle k="feature_outcomes" v={s} on={up} label="Outcome measures (Oswestry / DASH)" testid="ff-outcomes"/>
            {/* Iter 132 — Outcome measures visible to patients (self-serve).
                OFF by default → clinician-only, with clinician-assigned
                surveys still reaching the patient inbox. */}
            <Toggle k="feature_outcome_measures_patient_visible" v={s} on={up}
                    label="Show Outcome measures on the patient portal (self-serve)"
                    testid="ff-outcomes-patient"/>
            <Toggle k="feature_exercise_compliance" v={s} on={up} label="Exercise compliance tracking" testid="ff-compliance"/>
            <Toggle k="feature_ai_screen" v={s} on={up} label="AI red-flag screening" testid="ff-ai-screen"/>
            <Toggle k="feature_no_show_predictor" v={s} on={up} label="AI no-show predictor" testid="ff-noshow"/>
            <Toggle k="feature_memberships" v={s} on={up} label="Memberships & visit packs" testid="ff-memberships"/>
            <Toggle k="feature_clinician_profiles" v={s} on={up} label="Public clinician profiles (single-clinic: off)" testid="ff-clinician-profiles"/>
            <Toggle k="feature_marketing_email" v={s} on={up} label="Marketing email blasts" testid="ff-marketing"/>
            <Toggle k="feature_scheduled_promotions" v={s} on={up} label="Scheduled promotions" testid="ff-promotions"/>
            {/* Iter 133 — SMS reminders via Infobip. Consent + phone required. */}
            <Toggle k="feature_sms_reminders" v={s} on={up} label="SMS reminders (Infobip · consent-gated)" testid="ff-sms-reminders"/>
            <Toggle k="feature_appointment_alarm" v={s} on={up} label="Appointment alarm" testid="ff-alarm"/>
            <Toggle k="feature_student_tracker" v={s} on={up} label="Practical student tracker" testid="ff-students"/>
            <Toggle k="feature_storefront" v={s} on={up} label="Storefront (products for sale)" testid="ff-store"/>
            <Toggle k="feature_membership_hold" v={s} on={up} label="Patient self-hold on memberships" testid="ff-mem-hold"/>
            <Toggle k="feature_membership_self_renew" v={s} on={up} label="Patient self-renewal of memberships" testid="ff-mem-renew"/>
            <Toggle k="feature_runtime_i18n" v={s} on={up} label="AI runtime translation (PL / ES)" testid="ff-i18n"/>
            <Toggle k="feature_seasonal_theme" v={s} on={up} label="Seasonal landing theme" testid="ff-theme"/>
            <Toggle k="feature_video_checkins" v={s} on={up} label="At-home video check-ins" testid="ff-video"/>
            {user?.role === 'platform_admin' ? (
              <Toggle k="feature_massage_intake" v={s} on={up} label="Massage SOAP intake (platform-admin override)" testid="ff-massage-intake"/>
            ) : (
              <AddonRow
                feature={s.feature_massage_intake}
                to="/admin/massage-intake-addon"
                label="Massage SOAP intake"
                testid="ff-massage-intake-addon-cta"
              />
            )}
          </div>
        </details>

        {/* Iter 69 — Outcome measures (PROMs) per-survey toggles */}
        <div className="admin-card md:col-span-2" data-cat="clinical">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardCheck className="w-5 h-5 text-brand"/>
            <h3 className="font-heading font-semibold">Outcome surveys (PROMs)</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">
            Pick which patient-reported outcome measures your patients can complete from their portal. All four are evidence-based and auto-graded with trend graphs.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="proms-toggle-grid">
            <Toggle k="proms_odi_enabled" v={s} on={up} label="Oswestry Disability Index (lumbar)" testid="proms-toggle-odi"/>
            <Toggle k="proms_ndi_enabled" v={s} on={up} label="Neck Disability Index (cervical)" testid="proms-toggle-ndi"/>
            <Toggle k="proms_quickdash_enabled" v={s} on={up} label="QuickDASH (upper limb)" testid="proms-toggle-quickdash"/>
            <Toggle k="proms_vas_enabled" v={s} on={up} label="Pain VAS (rest / activity / night)" testid="proms-toggle-vas"/>
          </div>
        </div>

        <div className="admin-card md:col-span-2" data-cat="compliance">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Reception access</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">Per-area toggles for the receptionist role. Off by default for clinical / finances.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="reception-toggles">
            <Toggle k="reception_can_finances" v={s} on={up} label="Finances & invoices" testid="rcp-fin"/>
            <Toggle k="reception_can_clinical" v={s} on={up} label="Clinical (patient charts, intake)" testid="rcp-clinical"/>
            <Toggle k="reception_can_inventory" v={s} on={up} label="Inventory" testid="rcp-inv"/>
            <Toggle k="reception_can_memberships" v={s} on={up} label="Memberships" testid="rcp-mem"/>
            <Toggle k="reception_can_marketing" v={s} on={up} label="Marketing" testid="rcp-mkt"/>
          </div>
        </div>

        <details className="admin-card md:col-span-2" data-testid="thankyou-card" data-cat="marketing">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2 flex-wrap">
              <Sparkles className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Auto thank-you note</h3>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium ${s.feature_review_thank_you ? 'bg-[#1B5E20]/15 text-[#1B5E20]' : 'bg-fg-muted/15 text-fg-muted'}`} data-testid="thankyou-status">
                {s.feature_review_thank_you ? 'ON' : 'OFF'}
              </span>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <div className="surface border border-soft rounded-lg p-3 mt-3 mb-4 text-xs bg-[#F2EBDB]/40" data-testid="thankyou-help">
            <div className="font-heading font-semibold text-[#003D80] mb-1.5">How it works</div>
            <ol className="list-decimal list-inside space-y-1 text-fg-muted leading-relaxed">
              <li>A patient leaves a review on your <strong>/admin/reviews</strong> page or via the patient portal.</li>
              <li>When this feature is <strong>ON</strong> (toggle in "Feature flags" above), the system automatically emails a thank-you note to that reviewer — once per review, never twice.</li>
              <li>The email is <strong>private</strong> (sent directly to their inbox via Resend, never posted anywhere).</li>
              <li>Use <code className="bg-white px-1 py-0.5 rounded border border-soft">&#123;name&#125;</code> in the subject or body to personalize with the patient's first name.</li>
              <li>Default subject + body are pre-filled — edit them below and click Save (or wait for auto-save).</li>
            </ol>
            <div className="mt-2 text-[11px] text-fg-muted italic">
              Tip: keep it short, warm, and end with a soft call to share their experience publicly on Google. That's where review-driven growth comes from.
            </div>
          </div>
          <Field label="Subject (supports {name})">
            <input className="input w-full" value={s.review_thank_you_subject || ''}
                   onChange={e=>up('review_thank_you_subject', e.target.value)}
                   placeholder="Thank you for your review, {name}!"
                   data-testid="thankyou-subject"/>
          </Field>
          <Field label="Body (supports {name})">
            <textarea className="input min-h-[120px]" value={s.review_thank_you_body || ''}
                      onChange={e=>up('review_thank_you_body', e.target.value)}
                      placeholder={"Hi {name},\n\nThank you so much for taking the time to share your feedback…"}
                      data-testid="thankyou-body"/>
          </Field>
        </details>

        {/* Iter 133 — SMS reminders (Infobip). Sender name + delivery status. */}
        <details className="admin-card md:col-span-2" data-cat="communications" data-testid="sms-reminders-card">
          <summary className="flex items-center gap-2 cursor-pointer select-none">
            <Send className="w-5 h-5 text-brand"/>
            <h3 className="font-heading font-semibold">SMS reminders</h3>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium ${s.feature_sms_reminders ? 'bg-[#1B5E20]/15 text-[#1B5E20]' : 'bg-fg-muted/15 text-fg-muted'}`} data-testid="sms-status">
              {s.feature_sms_reminders ? 'ON' : 'OFF'}
            </span>
          </summary>
          <div className="surface border border-soft rounded-lg p-3 mt-3 mb-4 text-xs bg-[#F2EBDB]/40" data-testid="sms-help">
            <div className="font-heading font-semibold text-[#003D80] mb-1.5">How it works</div>
            <ol className="list-decimal list-inside space-y-1 text-fg-muted leading-relaxed">
              <li>Toggle <strong>SMS reminders</strong> ON in the Feature flags panel above.</li>
              <li>Each patient must explicitly grant the <strong>SMS reminders</strong> consent from their privacy settings — GDPR requires opt-in.</li>
              <li>Patient must have a valid mobile number saved in their profile (E.164 format like <code className="bg-white px-1 py-0.5 rounded border border-soft">+48501234567</code>).</li>
              <li>Once both are true, the system sends 4 SMSes: booking confirmation, 24-hour reminder, 2-hour reminder, and cancellation notice.</li>
              <li>Delivery status (delivered / undelivered / rejected) is logged per-message under Admin → System status.</li>
            </ol>
            <div className="mt-2 text-[11px] text-fg-muted italic">
              Trial accounts can only message verified test numbers registered in your Infobip portal. Once you go live, buy an alphanumeric sender ID (e.g. <code>PHYSIOGENIC</code>) and put it below.
            </div>
          </div>
          <Field label="Sender name (max 24 chars)">
            <input className="input w-full" value={s.sms_sender_name || ''}
                   onChange={e => up('sms_sender_name', e.target.value.slice(0, 24))}
                   placeholder="ServiceSMS (default) or your approved alphanumeric ID"
                   maxLength={24}
                   data-testid="sms-sender-name"/>
          </Field>
          <div className="text-[11px] text-fg-muted mt-2">
            Provider: <strong>Infobip</strong> · EU data residency ·
            {' '}Alphanumeric senders often need approval — start with <code>ServiceSMS</code>.
          </div>
          <TestSmsBox />
        </details>

        {/* Service disciplines — drives the patient-booking dropdown + per-discipline templates. */}
        <div className="admin-card md:col-span-2" data-testid="service-disciplines-card" data-cat="clinical">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Service disciplines</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">
            Which medical professions does this clinic offer? Patients pick from this list when booking.
            Each discipline gets its own assessment template + AI prompts.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="discipline-toggles">
            {DISCIPLINES.map(d => {
              const list = s.service_disciplines || ['physiotherapy'];
              const on = list.includes(d.key);
              return (
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  key={d.key}
                  className={`surface border rounded-lg p-3 cursor-pointer transition flex items-center gap-3 w-full text-left ${on ? 'border-brand bg-brand/5' : 'border-soft hover:border-fg/30'}`}
                  data-testid={`disc-${d.key}`}
                  onClick={()=>{
                    const next = on ? list.filter(x => x !== d.key) : [...list, d.key];
                    up('service_disciplines', next.length ? next : ['physiotherapy']);
                  }}
                >
                  <span aria-hidden="true"
                        className={`relative inline-block w-9 h-5 shrink-0 rounded-full transition-colors ${on ? 'bg-brand' : 'bg-fg-muted/30'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : 'translate-x-0'}`}/>
                  </span>
                  <span className="flex-1">
                    <span className="text-sm font-medium block">{d.label}</span>
                    <span className="text-[11px] text-fg-muted block">{d.sub}</span>
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${on ? 'text-brand' : 'text-fg-muted'}`}>{on ? 'ON' : 'OFF'}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 text-[11px] text-fg-muted italic">
            Tip: enable one discipline for a focused practice, or any combination for a multi-disciplinary clinic.
          </div>
        </div>

        {/* Examination format (SOAP vs DAP) + per-section toggles. */}
        <details className="admin-card md:col-span-2" data-testid="exam-format-card" data-cat="clinical">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Clinical examination format</h3>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <p className="text-xs text-fg-muted mt-3 mb-3">
            Choose the default notes template — <strong>SOAP</strong> (Subjective / Objective / Assessment / Plan)
            is the international physio standard. <strong>DAP</strong> (Data / Assessment / Plan) is more common
            for osteopathy and some chiropractic schools.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4" data-testid="exam-format-picker">
            {[
              { k: 'SOAP', label: 'SOAP', sub: 'Subjective · Objective · Assessment · Plan' },
              { k: 'DAP',  label: 'DAP',  sub: 'Data · Assessment · Plan' },
            ].map(f => {
              const active = (s.default_exam_format || 'SOAP') === f.k;
              return (
                <button key={f.k} type="button" onClick={()=>up('default_exam_format', f.k)}
                        className={`p-3 rounded-lg border text-left transition ${active ? 'border-brand ring-2 ring-brand/20 bg-brand/5' : 'border-soft hover:border-fg/30'}`}
                        data-testid={`exam-fmt-${f.k.toLowerCase()}`}>
                  <div className="text-sm font-medium">{f.label}</div>
                  <div className="text-[11px] text-fg-muted mt-0.5">{f.sub}</div>
                  {active && <div className="text-[10px] text-brand mt-1 uppercase tracking-wider">Default</div>}
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-4 pt-3 border-t border-soft">
            <div>
              <div className="text-xs uppercase tracking-wider text-fg-muted mb-2 font-medium">SOAP sections visible</div>
              <div className="grid gap-2" data-testid="soap-section-toggles">
                <Toggle k="soap_section_subjective" v={s} on={up} label="S — Subjective (patient report)" testid="soap-s"/>
                <Toggle k="soap_section_objective"  v={s} on={up} label="O — Objective (exam findings)"  testid="soap-o"/>
                <Toggle k="soap_section_assessment" v={s} on={up} label="A — Assessment (diagnosis)"     testid="soap-a"/>
                <Toggle k="soap_section_plan"       v={s} on={up} label="P — Plan (treatment + goals)"   testid="soap-p"/>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-fg-muted mb-2 font-medium">DAP sections visible</div>
              <div className="grid gap-2" data-testid="dap-section-toggles">
                <Toggle k="dap_section_data"       v={s} on={up} label="D — Data (subjective + objective merged)" testid="dap-d"/>
                <Toggle k="dap_section_assessment" v={s} on={up} label="A — Assessment (clinical impression)"      testid="dap-a"/>
                <Toggle k="dap_section_plan"       v={s} on={up} label="P — Plan (treatment + follow-up)"          testid="dap-p"/>
              </div>
            </div>
          </div>
        </details>

        {/* ICD coding system — admin can opt into ICD-11 (WHO 2022). */}
        <div className="admin-card md:col-span-2" data-testid="icd-coding-card" data-cat="compliance">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Diagnosis coding</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">
            ICD-10 is the global default. Enable <strong>ICD-11</strong> (released by WHO in 2022) to also offer
            the newer coding system in the diagnosis pickers — both stay searchable side-by-side.
          </p>
          <Toggle k="feature_icd11" v={s} on={up} label="Enable ICD-11 (WHO 2022) alongside ICD-10" testid="ff-icd11"/>
        </div>

        {/* Objective examination section toggles — only shown when the module is enabled. */}
        {/* Setup-manual video URLs — admin pastes a YouTube/Vimeo/MP4 link
            per onboarding step. Players appear inline on /admin/setup-manual. */}
        <EmailDeliverabilityCard/>

        <details className="admin-card md:col-span-2" data-testid="setup-videos-card" data-cat="operations">
          <summary className="cursor-pointer list-none flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold">Setup-manual videos</h3>
              <p className="text-xs text-fg-muted">Paste a YouTube, Vimeo, or MP4 URL per onboarding step. Leave blank to hide the player.</p>
            </div>
            <span className="text-xs text-fg-muted">click to {Object.keys(s.setup_videos || {}).filter(k => (s.setup_videos || {})[k]).length} configured</span>
          </summary>
          <div className="mt-4 grid gap-2">
            {[
              { k: 'create-account', label: 'Step 1 — Create your clinic account' },
              { k: 'branding',       label: 'Step 2 — Branding & contact info' },
              { k: 'solo-vs-multi',  label: 'Step 3 — Solo or multi-clinic' },
              { k: 'services-pricing', label: 'Step 4 — Services & pricing' },
              { k: 'staff-invites',  label: 'Step 5 — Invite your staff' },
              { k: 'business-hours', label: 'Step 6 — Business hours & calendar' },
              { k: 'payments',       label: 'Step 7 — Connect Stripe' },
              { k: 'feature-flags',  label: 'Step 8 — Feature flags' },
              { k: 'first-booking',  label: 'Step 9 — Test the booking flow' },
              { k: 'go-live',        label: 'Step 10 — Go live' },
              { k: 'hr-setup',       label: 'Step 17 — HR: hiring, banking & payroll' },
              { k: 'package-builder', label: 'Step 18 — Build treatment packages' },
              { k: 'time-off-approvals', label: 'Ops manual — Time-off requests & approvals' },
              { k: 'package-selling', label: 'Ops manual — Build & sell packages' },
              { k: 'hr-hiring-package', label: 'Ops manual — Creating a hiring package' },
              { k: 'payroll-runs',   label: 'Ops manual — Payroll runs' },
              { k: 'paystub-payment', label: 'Ops manual — Pay stubs & payments' },
            ].map(step => (
              <label key={step.k} className="block">
                <div className="text-xs text-fg-muted mb-1">{step.label}</div>
                <input
                  className="input text-xs font-mono"
                  type="url"
                  placeholder="https://youtube.com/watch?v=… or .mp4 URL (leave blank to hide)"
                  value={(s.setup_videos || {})[step.k] || ''}
                  onChange={e => up('setup_videos', { ...(s.setup_videos || {}), [step.k]: e.target.value })}
                  data-testid={`setup-video-url-${step.k}`}
                />
              </label>
            ))}
          </div>
        </details>

        {(s.feature_objective_examination ?? true) && (
        <details className="admin-card md:col-span-2" data-cat="clinical" data-testid="objective-exam-card">
          <summary className="cursor-pointer flex items-center justify-between -m-2 p-2 rounded hover:bg-[#FAF6EE]/50 transition list-none">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand"/>
              <h3 className="font-heading font-semibold">Objective examination</h3>
            </div>
            <span className="text-xs text-fg-muted settings-chevron">▾</span>
          </summary>
          <p className="text-xs text-fg-muted mt-3 mb-3">Turn individual Part 2 sections on/off for this clinic.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="obj-section-toggles">
            <Toggle k="obj_section_vitals" v={s} on={up} label="Vital signs" testid="obj-vitals"/>
            <Toggle k="obj_section_auscultation" v={s} on={up} label="Auscultation" testid="obj-ausc"/>
            <Toggle k="obj_section_observation" v={s} on={up} label="Physical observation" testid="obj-obs"/>
            <Toggle k="obj_section_mmt" v={s} on={up} label="Manual muscle testing" testid="obj-mmt"/>
            <Toggle k="obj_section_rom" v={s} on={up} label="Active / Passive ROM (goniometer)" testid="obj-rom"/>
            <Toggle k="obj_section_flexibility" v={s} on={up} label="Flexibility (L / R)" testid="obj-flex"/>
            <Toggle k="obj_section_neurology" v={s} on={up} label="Neurology" testid="obj-neuro"/>
            <Toggle k="obj_section_balance" v={s} on={up} label="Balance" testid="obj-bal"/>
            <Toggle k="obj_section_cardio" v={s} on={up} label="Cardiovascular" testid="obj-cardio"/>
            <Toggle k="obj_section_one_rm" v={s} on={up} label="1RM test" testid="obj-1rm"/>
            <Toggle k="obj_section_functional" v={s} on={up} label="Functional tests" testid="obj-func"/>
            <Toggle k="obj_section_lsi" v={s} on={up} label="Limb Symmetry Index" testid="obj-lsi"/>
            <Toggle k="obj_section_special" v={s} on={up} label="Special evidence-based tests" testid="obj-special"/>
            <Toggle k="obj_section_diagnosis" v={s} on={up} label="Diagnosis · plan · treatment + signature" testid="obj-dx"/>
          </div>
        </details>
        )}

        <div className="admin-card md:col-span-2" data-cat="operations">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Clinic operating country</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">Drives default invoice format, locale, and tax rules.</p>
          <Field label="Country">
            <select className="input w-full md:max-w-md" value={s.clinic_country || 'PL'}
                    onChange={e=>up('clinic_country', e.target.value)}
                    data-testid="clinic-country">
              <optgroup label="Europe">
                <option value="PL">🇵🇱 Poland</option>
                <option value="DE">🇩🇪 Germany</option>
                <option value="FR">🇫🇷 France</option>
                <option value="ES">🇪🇸 Spain</option>
                <option value="IT">🇮🇹 Italy</option>
                <option value="NL">🇳🇱 Netherlands</option>
                <option value="BE">🇧🇪 Belgium</option>
                <option value="LU">🇱🇺 Luxembourg</option>
                <option value="IE">🇮🇪 Ireland</option>
                <option value="PT">🇵🇹 Portugal</option>
                <option value="CH">🇨🇭 Switzerland</option>
                <option value="AT">🇦🇹 Austria</option>
                <option value="UK">🇬🇧 United Kingdom</option>
                <option value="SE">🇸🇪 Sweden</option>
                <option value="NO">🇳🇴 Norway</option>
                <option value="DK">🇩🇰 Denmark</option>
                <option value="FI">🇫🇮 Finland</option>
                <option value="IS">🇮🇸 Iceland</option>
                <option value="CZ">🇨🇿 Czechia</option>
                <option value="SK">🇸🇰 Slovakia</option>
                <option value="HU">🇭🇺 Hungary</option>
                <option value="RO">🇷🇴 Romania</option>
                <option value="BG">🇧🇬 Bulgaria</option>
                <option value="GR">🇬🇷 Greece</option>
                <option value="HR">🇭🇷 Croatia</option>
                <option value="SI">🇸🇮 Slovenia</option>
                <option value="EE">🇪🇪 Estonia</option>
                <option value="LV">🇱🇻 Latvia</option>
                <option value="LT">🇱🇹 Lithuania</option>
                <option value="MT">🇲🇹 Malta</option>
                <option value="CY">🇨🇾 Cyprus</option>
              </optgroup>
              <optgroup label="Americas">
                <option value="US">🇺🇸 United States</option>
                <option value="CA">🇨🇦 Canada</option>
                <option value="MX">🇲🇽 Mexico</option>
                <option value="BR">🇧🇷 Brazil</option>
                <option value="AR">🇦🇷 Argentina</option>
                <option value="CL">🇨🇱 Chile</option>
                <option value="CO">🇨🇴 Colombia</option>
                <option value="PE">🇵🇪 Peru</option>
                <option value="VE">🇻🇪 Venezuela</option>
                <option value="UY">🇺🇾 Uruguay</option>
                <option value="EC">🇪🇨 Ecuador</option>
              </optgroup>
              <optgroup label="Asia-Pacific">
                <option value="AU">🇦🇺 Australia</option>
                <option value="NZ">🇳🇿 New Zealand</option>
                <option value="JP">🇯🇵 Japan</option>
                <option value="KR">🇰🇷 South Korea</option>
                <option value="SG">🇸🇬 Singapore</option>
                <option value="HK">🇭🇰 Hong Kong</option>
                <option value="TW">🇹🇼 Taiwan</option>
                <option value="MY">🇲🇾 Malaysia</option>
                <option value="TH">🇹🇭 Thailand</option>
                <option value="PH">🇵🇭 Philippines</option>
                <option value="ID">🇮🇩 Indonesia</option>
                <option value="VN">🇻🇳 Vietnam</option>
                <option value="IN">🇮🇳 India</option>
                <option value="PK">🇵🇰 Pakistan</option>
                <option value="BD">🇧🇩 Bangladesh</option>
                <option value="LK">🇱🇰 Sri Lanka</option>
                <option value="AE">🇦🇪 United Arab Emirates</option>
                <option value="SA">🇸🇦 Saudi Arabia</option>
                <option value="QA">🇶🇦 Qatar</option>
                <option value="KW">🇰🇼 Kuwait</option>
                <option value="IL">🇮🇱 Israel</option>
                <option value="TR">🇹🇷 Turkey</option>
              </optgroup>
              <optgroup label="Africa">
                <option value="ZA">🇿🇦 South Africa</option>
                <option value="EG">🇪🇬 Egypt</option>
                <option value="NG">🇳🇬 Nigeria</option>
                <option value="KE">🇰🇪 Kenya</option>
                <option value="MA">🇲🇦 Morocco</option>
                <option value="TN">🇹🇳 Tunisia</option>
                <option value="GH">🇬🇭 Ghana</option>
              </optgroup>
              <optgroup label="Caribbean">
                <option value="KN">🇰🇳 Saint Kitts and Nevis</option>
                <option value="JM">🇯🇲 Jamaica</option>
                <option value="DO">🇩🇴 Dominican Republic</option>
                <option value="TT">🇹🇹 Trinidad and Tobago</option>
                <option value="BB">🇧🇧 Barbados</option>
                <option value="BS">🇧🇸 Bahamas</option>
              </optgroup>
            </select>
          </Field>
        </div>

        {/* Multi-location & backup — placed right above Clinic Locations so admins
            see the toggle that GATES the locations card first. */}
        <div className="admin-card md:col-span-2" data-testid="multiloc-backup-card" data-cat="operations">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Multi-location & backup</h3>
          </div>
          <p className="text-xs text-fg-muted mb-3">
            Multi-location lets you manage multiple physical branches under one tenant. Cloud backup
            takes a nightly snapshot of your data and stores it to Backblaze B2 (encrypted).
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <Toggle k="feature_multi_location" v={s} on={up} label="Multi-location (clinic branches)" testid="ml-toggle"/>
            <Toggle k="feature_backup" v={s} on={up} label="Cloud backup (Backblaze B2)" testid="bk-toggle"/>
          </div>
          {s.feature_backup && <BackupCard/>}
        </div>

        {/* Multi-location branches — relevant whenever the tenant is in
            multi-clinic mode. Shows a CTA in solo mode to upgrade. */}
        <ClinicLocationsCard
          enabled={!!s.feature_multi_location}
          onEnable={() => up('feature_multi_location', true)}
        />

        {/* Platform-wide clinic directory — visible only to platform_admin.
            Shows every tenant (Solo / Multi badge, plan, status, last login). */}
        {user?.role === 'platform_admin' && <PlatformClinicsCard/>}
      </div>

      {/* Iter 133 — Danger zone: wipe all clinic data. Only real clinics
          (not "default") can hit this endpoint; the backend also requires
          the admin to re-type the clinic name for confirmation. */}
      <ClinicWipeCard/>
      <ClinicExportCard/>
      <ClinicDeleteCard/>

      {/* Sticky save footer — always visible while scrolling.
          Saves on click + auto-saves 1.5s after the last edit (debounced). */}
      <div className="fixed bottom-0 left-0 right-0 md:left-64 z-40 bg-white/95 backdrop-blur border-t border-soft px-4 md:px-8 py-2.5 flex items-center justify-between gap-3" data-testid="settings-sticky-bar">
        <StateIndicator/>
        <button onClick={save} disabled={saving || !isDirty}
                className={`btn-primary inline-flex items-center gap-2 text-sm ${!isDirty ? 'opacity-50 cursor-default' : ''}`}
                data-testid="save-settings-sticky">
          <Save className="w-3.5 h-3.5"/> {saving ? 'Saving…' : (isDirty ? 'Save now' : 'All saved')}
        </button>
      </div>
    </div>
  );
}

function ClinicWipeCard() {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const load = () => {
    api.get('/clinic/wipe/preview')
      .then(r => setPreview(r.data))
      .catch(e => setErr(e.response?.data?.detail || e.message));
  };
  useEffect(() => { load(); }, []);

  const openDialog = () => {
    setErr(''); setConfirm(''); setReason(''); setResult(null);
    setOpen(true);
    load();
  };

  const wipe = async () => {
    setErr(''); setBusy(true);
    try {
      const { data } = await api.post('/clinic/wipe', {
        confirm_clinic_name: confirm,
        reason: reason || null,
      });
      setResult(data);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const clinicName = preview?.clinic_name || '';
  const rows = preview?.preview?.rows || [];
  const total = preview?.preview?.total || 0;

  return (
    <div className="mt-6 rounded-lg border border-[#B83A3A]/40 bg-[#B83A3A]/5 p-6" data-testid="danger-zone-card">
      <div className="flex items-start gap-4 mb-3">
        <div className="w-10 h-10 rounded-md bg-[#B83A3A]/20 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-[#B83A3A]"/>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] text-[#B83A3A] font-semibold mb-1">Danger zone</div>
          <h3 className="font-heading font-bold text-lg mb-1">Wipe all clinic data</h3>
          <p className="text-sm text-fg-muted" key={`dz-desc-${clinicName}`}>
            Permanently deletes every patient, appointment, invoice, document,
            clinician, inventory item, form, and record inside{' '}
            <strong>{clinicName || 'this clinic'}</strong>. Your admin account,
            clinic settings, and shared library templates are preserved so you
            can start fresh. <strong>This cannot be undone.</strong>
          </p>
        </div>
      </div>
      {total > 0 ? (
        <div className="text-xs text-fg-muted mb-3">
          <span className="font-semibold text-fg">{total.toLocaleString()}</span>{' '}
          record(s) currently exist across{' '}
          <span className="font-semibold text-fg">{rows.length}</span>{' '}
          collection(s).
        </div>
      ) : err && !preview ? (
        <div className="text-xs text-[#B83A3A] mb-3" data-testid="wipe-preview-error">{err}</div>
      ) : (
        <div className="text-xs text-fg-muted mb-3">No operational data to wipe — clinic is already clean.</div>
      )}
      <button
        type="button"
        onClick={openDialog}
        disabled={total === 0}
        className={`inline-flex items-center gap-2 text-sm rounded-md px-4 py-2 border transition ${
          total === 0
            ? 'border-soft text-fg-muted cursor-not-allowed opacity-60'
            : 'border-[#B83A3A] bg-[#B83A3A] text-white hover:bg-[#8A2626] hover:border-[#8A2626]'
        }`}
        data-testid="open-wipe-dialog"
      >
        <AlertCircle className="w-4 h-4"/> Wipe clinic data…
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="wipe-dialog">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-2xl" key={result ? 'wipe-result' : 'wipe-confirm'}>
            {!result ? (
              <div key="confirm-body">
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle className="w-6 h-6 text-[#B83A3A] flex-shrink-0 mt-1"/>
                  <div>
                    <h3 className="font-heading font-bold text-lg">Confirm data wipe</h3>
                    <p className="text-sm text-fg-muted mt-1">
                      You're about to permanently delete{' '}
                      <strong>{total.toLocaleString()}</strong> record(s) from{' '}
                      <strong>{clinicName}</strong>. This action is irreversible.
                    </p>
                  </div>
                </div>

                {rows.length > 0 && (
                  <div className="max-h-40 overflow-y-auto surface border border-soft rounded-md p-3 mb-4" data-testid="wipe-preview">
                    {rows.map(r => (
                      <div key={r.collection} className="flex items-center justify-between text-xs py-1">
                        <span className="text-fg-muted">{r.collection}</span>
                        <span className="font-mono font-semibold">{r.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                <label className="block mb-3">
                  <div className="text-xs text-fg-muted mb-1">
                    Retype your clinic name (<strong className="text-fg">{clinicName}</strong>) to confirm:
                  </div>
                  <input
                    type="text"
                    className="input"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    autoFocus
                    data-testid="wipe-confirm-name"
                  />
                </label>

                <label className="block mb-4">
                  <div className="text-xs text-fg-muted mb-1">Reason (optional, for the audit log)</div>
                  <input
                    type="text"
                    className="input"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. going live after testing"
                    data-testid="wipe-reason"
                  />
                </label>

                {err && (
                  <div className="rounded-md bg-[#B83A3A]/10 border border-[#B83A3A]/30 text-[#B83A3A] text-xs px-3 py-2 mb-3" data-testid="wipe-error">
                    {err}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setOpen(false)} className="btn-secondary text-sm" data-testid="wipe-cancel">Cancel</button>
                  <button
                    type="button"
                    onClick={wipe}
                    disabled={busy || confirm.trim().toLowerCase() !== clinicName.trim().toLowerCase()}
                    className="text-sm rounded-md px-4 py-2 border border-[#B83A3A] bg-[#B83A3A] text-white hover:bg-[#8A2626] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="wipe-confirm-btn"
                  >
                    {busy ? 'Wiping…' : `Delete ${total.toLocaleString()} record(s)`}
                  </button>
                </div>
              </div>
            ) : (
              <div key="result-body">
                <div className="flex items-start gap-3 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1"/>
                  <div>
                    <h3 className="font-heading font-bold text-lg">Clinic wiped</h3>
                    <p className="text-sm text-fg-muted mt-1">
                      Removed{' '}
                      <strong>{result.total_documents_removed.toLocaleString()}</strong>{' '}
                      record(s) across{' '}
                      <strong>{result.deleted.length}</strong>{' '}
                      collection(s). Your account and clinic settings are intact.
                    </p>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto surface border border-soft rounded-md p-3 mb-4">
                  {result.deleted.map(d => (
                    <div key={d.collection} className="flex items-center justify-between text-xs py-1">
                      <span className="text-fg-muted">{d.collection}</span>
                      <span className="font-mono font-semibold">−{d.deleted.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setOpen(false); window.location.reload(); }}
                    className="btn-primary text-sm"
                    data-testid="wipe-done"
                  >
                    Done — refresh dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function ClinicExportCard() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);

  const download = async () => {
    setErr(''); setOk(false); setBusy(true);
    try {
      const resp = await api.get('/clinic/export', { responseType: 'blob', timeout: 120000 });
      const cd = resp.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'physiogenic-export.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setOk(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Export failed — please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-[#003D80]/30 bg-[#003D80]/5 p-6" data-testid="export-data-card">
      <div className="flex items-start gap-4 mb-3">
        <div className="w-10 h-10 rounded-md bg-[#003D80]/15 flex items-center justify-center flex-shrink-0">
          <ClipboardCheck className="w-5 h-5 text-[#003D80]"/>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] text-[#003D80] font-semibold mb-1">GDPR Art. 20 · Data portability</div>
          <h3 className="font-heading font-bold text-lg mb-1">Export my data</h3>
          <p className="text-sm text-fg-muted">
            Download a complete copy of everything in your clinic — patients,
            appointments, invoices, documents metadata, staff and settings — as a
            ZIP of JSON files (plus CSVs for the main tables). Security secrets are
            redacted. <strong>Strongly recommended before wiping or deleting.</strong>
            {' '}The bundle contains medical data — store it securely.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-2 text-sm rounded-md px-4 py-2 border border-[#003D80] bg-[#003D80] text-white hover:bg-[#062B57] transition disabled:opacity-50"
          data-testid="export-data-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
          {busy ? 'Preparing bundle…' : 'Download my data (ZIP)'}
        </button>
        {ok && (
          <span className="text-xs text-[#1B5E20] inline-flex items-center gap-1" data-testid="export-data-ok">
            <CheckCircle2 className="w-3.5 h-3.5"/> Export downloaded
          </span>
        )}
        {err && (
          <span className="text-xs text-[#B83A3A]" data-testid="export-data-error">{err}</span>
        )}
      </div>
    </div>
  );
}


function ClinicDeleteCard() {
  const { logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const load = () => {
    api.get('/clinic/delete/preview')
      .then(r => setPreview(r.data))
      .catch(e => setErr(e.response?.data?.detail || e.message));
  };
  useEffect(() => { load(); }, []);

  const openDialog = () => {
    setErr(''); setConfirm(''); setAck(false); setReason(''); setDone(false);
    setOpen(true);
    load();
  };

  const doDelete = async () => {
    setErr(''); setBusy(true);
    try {
      await api.post('/clinic/delete', {
        confirm_clinic_name: confirm,
        acknowledge: ack,
        reason: reason || null,
      });
      setDone(true);
      setTimeout(async () => {
        try { await logout(); } catch (e) {}
        window.location.href = '/';
      }, 3500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const clinicName = preview?.clinic_name || '';
  const total = preview?.preview?.total || 0;
  const rows = preview?.preview?.rows || [];

  return (
    <div className="mt-4 rounded-lg border border-[#7A1F1F]/50 bg-[#7A1F1F]/5 p-6" data-testid="delete-account-card">
      <div className="flex items-start gap-4 mb-3">
        <div className="w-10 h-10 rounded-md bg-[#7A1F1F]/20 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-[#7A1F1F]"/>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] text-[#7A1F1F] font-semibold mb-1">GDPR Art. 17 · Right to erasure</div>
          <h3 className="font-heading font-bold text-lg mb-1">Delete account &amp; clinic</h3>
          <p className="text-sm text-fg-muted" key={`del-desc-${clinicName}`}>
            Permanently erases <strong>{clinicName || 'this clinic'}</strong> and
            everything in it — all patient records, all staff accounts,{' '}
            <strong>including your own admin account</strong>, clinic settings and
            the clinic itself. Any active subscription is cancelled so you won't
            be billed again. You will be logged out immediately.{' '}
            <strong>This cannot be undone.</strong>
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={openDialog}
        className="inline-flex items-center gap-2 text-sm rounded-md px-4 py-2 border border-[#7A1F1F] text-[#7A1F1F] hover:bg-[#7A1F1F] hover:text-white transition"
        data-testid="open-delete-account-dialog"
      >
        <AlertCircle className="w-4 h-4"/> Delete account &amp; clinic…
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-account-dialog">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-2xl">
            {!done ? (
              <div key="del-confirm-body">
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle className="w-6 h-6 text-[#7A1F1F] flex-shrink-0 mt-1"/>
                  <div>
                    <h3 className="font-heading font-bold text-lg">Delete {clinicName}?</h3>
                    <p className="text-sm text-fg-muted mt-1">
                      This erases <strong>{total.toLocaleString()}</strong> record(s)
                      across <strong>{rows.length}</strong> collection(s), cancels any
                      active subscription, and deletes your login. There is no recovery.
                    </p>
                    <p className="text-xs text-[#003D80] mt-2 font-medium">
                      Safety net: use “Export my data” above to download a full copy first.
                    </p>
                  </div>
                </div>

                {rows.length > 0 && (
                  <div className="max-h-32 overflow-y-auto surface border border-soft rounded-md p-3 mb-4" data-testid="delete-account-preview">
                    {rows.map(r => (
                      <div key={r.collection} className="flex items-center justify-between text-xs py-1">
                        <span className="text-fg-muted">{r.collection}</span>
                        <span className="font-mono font-semibold">{r.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}

                <label className="block mb-3">
                  <div className="text-xs text-fg-muted mb-1">
                    Retype your clinic name (<strong className="text-fg">{clinicName}</strong>) to confirm:
                  </div>
                  <input type="text" className="input" value={confirm}
                         onChange={e => setConfirm(e.target.value)} autoFocus
                         data-testid="delete-account-confirm-name"/>
                </label>

                <label className="flex items-start gap-2 mb-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={ack}
                         onChange={e => setAck(e.target.checked)}
                         data-testid="delete-account-ack"/>
                  <span className="text-xs text-fg-muted">
                    I understand this permanently erases all data under GDPR Art. 17
                    and cannot be undone. A minimal deletion record is retained for
                    legal accountability (Art. 5(2)).
                  </span>
                </label>

                <label className="block mb-4">
                  <div className="text-xs text-fg-muted mb-1">Reason (optional)</div>
                  <input type="text" className="input" value={reason}
                         onChange={e => setReason(e.target.value)}
                         placeholder="e.g. closing the practice"
                         data-testid="delete-account-reason"/>
                </label>

                {err && (
                  <div className="rounded-md bg-[#7A1F1F]/10 border border-[#7A1F1F]/30 text-[#7A1F1F] text-xs px-3 py-2 mb-3" data-testid="delete-account-error">
                    {err}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setOpen(false)} className="btn-secondary text-sm" data-testid="delete-account-cancel">Cancel</button>
                  <button
                    type="button"
                    onClick={doDelete}
                    disabled={busy || !ack || confirm.trim().toLowerCase() !== clinicName.trim().toLowerCase()}
                    className="text-sm rounded-md px-4 py-2 border border-[#7A1F1F] bg-[#7A1F1F] text-white hover:bg-[#5C1616] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="delete-account-confirm-btn"
                  >
                    {busy ? 'Deleting…' : 'Permanently delete everything'}
                  </button>
                </div>
              </div>
            ) : (
              <div key="del-done-body" className="text-center py-6" data-testid="delete-account-done">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3"/>
                <h3 className="font-heading font-bold text-lg mb-1">Account &amp; clinic deleted</h3>
                <p className="text-sm text-fg-muted">
                  All data has been erased. Signing you out…
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function AddonRow({ feature, to, label, testid }) {
  // Iter 131/132 — Generic read-only status pill for a paid add-on. Replaces
  // the free feature toggle. State is server-driven — cannot be flipped by
  // clinic admins. Click routes to the addon's purchase / activation page.
  const enabled = !!feature;
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 surface border rounded-lg p-3 transition w-full text-left hover:border-brand ${
        enabled ? 'border-emerald-400 bg-emerald-50/40' : 'border-soft'
      }`}
      data-testid={testid}
    >
      <span
        aria-hidden="true"
        className={`inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-md border-2 ${
          enabled ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-fg-muted/40 text-fg-muted bg-white'
        }`}
        title={enabled ? 'Active — click to manage' : 'Not activated — click to purchase'}
      >
        {enabled ? (
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 11 8 15 16 6"/>
          </svg>
        ) : (
          <span className="text-xs font-bold">€</span>
        )}
      </span>
      <span className="text-sm text-fg leading-tight flex-1">
        {label} <span className="text-[11px] text-fg-muted font-normal">· paid add-on</span>
      </span>
      <span className={`text-[10px] uppercase tracking-wider font-semibold ${enabled ? 'text-emerald-700' : 'text-brand'}`}>
        {enabled ? 'Active · manage' : 'View pricing'}
      </span>
    </Link>
  );
}

// Iter 132 — legacy alias so older refs (if any surface) still resolve.
const FitnessAddonRow = ({ enabled }) => (
  <AddonRow feature={enabled} to="/admin/fitness-addon" label="Fitness & wellness" testid="ff-fitness-addon-cta"/>
);

function Toggle({ k, v, on, label, testid }) {
  const checked = !!v[k];
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => on(k, !checked)}
      data-testid={testid}
      className={`flex items-center gap-3 cursor-pointer surface border rounded-lg p-3 transition w-full text-left ${
        checked ? 'border-brand bg-brand/5' : 'border-soft hover:border-brand'
      }`}
    >
      {/* Big checkbox + pill — the checkmark icon makes ON/OFF unambiguous at
          a glance (users complained the pill alone was hard to read). */}
      <span
        aria-hidden="true"
        className={`relative inline-flex items-center justify-center w-6 h-6 shrink-0 rounded-md border-2 transition-colors ${
          checked ? 'bg-brand border-brand text-white' : 'bg-transparent border-fg-muted/40 text-transparent'
        }`}
      >
        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="4 11 8 15 16 6"/>
        </svg>
      </span>
      <span className="text-sm text-fg leading-tight flex-1">{label}</span>
      <span className={`text-[10px] uppercase tracking-wider font-semibold ${checked ? 'text-brand' : 'text-fg-muted'}`}>
        {checked ? 'ON' : 'OFF'}
      </span>
    </button>
  );
}

function CurrencyAdder({ selected, onAdd }) {
  const [q, setQ] = useState('');
  const matches = q.trim()
    ? WORLD_CURRENCIES.filter(c =>
        !selected.includes(c.code) &&
        (c.code.toLowerCase().includes(q.trim().toLowerCase()) ||
         c.name.toLowerCase().includes(q.trim().toLowerCase()))
      ).slice(0, 8)
    : [];
  return (
    <div className="relative max-w-sm" data-testid="currency-adder">
      <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"/>
      <input className="input w-full pl-9 text-sm" value={q}
             onChange={e => setQ(e.target.value)}
             placeholder="Add currency — search by code or country (e.g. CHF, Yen, Peso)…"
             data-testid="currency-search"/>
      {matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-soft rounded-md shadow-lg overflow-hidden" data-testid="currency-results">
          {matches.map(c => (
            <button key={c.code} type="button"
                    onClick={() => { onAdd(c.code); setQ(''); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#FAF6EE] flex items-center justify-between"
                    data-testid={`add-cur-${c.code}`}>
              <span className="font-mono font-semibold">{c.code}</span>
              <span className="text-xs text-fg-muted">{c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TestSmsBox() {
  const [phone, setPhone] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null); // {ok, msg}
  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post('/sms/test', { phone: phone.trim() });
      setResult({ ok: true, msg: `Sent (id ${data.message_id}, status ${data.status}).` });
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || 'Send failed' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="surface border border-soft rounded-lg p-3 mt-4" data-testid="test-sms-box">
      <div className="font-heading font-semibold text-sm mb-1">Send a test SMS</div>
      <p className="text-[11px] text-fg-muted mb-2">
        Verify your setup end-to-end: send yourself a message, then watch it flip to
        {' '}<code>DELIVERED_TO_HANDSET</code> under{' '}
        <Link to="/admin/system-status" className="text-brand underline" data-testid="test-sms-status-link">System status</Link>.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="input flex-1" value={phone}
               onChange={e => setPhone(e.target.value)}
               placeholder="+48501234567 (E.164 format)"
               data-testid="test-sms-phone"/>
        <button className="btn-primary whitespace-nowrap disabled:opacity-50"
                disabled={busy || !phone.trim()}
                onClick={send}
                data-testid="test-sms-send-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline"/> : <Send className="w-4 h-4 inline mr-1"/>}
          {busy ? ' Sending…' : 'Send test SMS'}
        </button>
      </div>
      {result && (
        <div className={`mt-2 text-xs flex items-center gap-1.5 ${result.ok ? 'text-[#1B5E20]' : 'text-red-700'}`}
             data-testid="test-sms-result">
          {result.ok ? <CheckCircle2 className="w-3.5 h-3.5"/> : <AlertCircle className="w-3.5 h-3.5"/>}
          {result.msg}
        </div>
      )}
    </div>
  );
}

function BackupCard() {
  const [busy, setBusy] = React.useState(false);
  const [hist, setHist] = React.useState([]);
  const load = () => api.get('/admin/backup/history').then(r => setHist(r.data || [])).catch(() => {});
  React.useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/backup/backblaze');
      alert(`Backup queued — id ${data.backup_id}`);
      load();
    } catch (e) {
      alert('Backup failed: ' + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-soft" data-testid="backup-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-medium text-sm">Backblaze B2 cloud backup</div>
          <div className="text-[11px] text-fg-muted">Nightly mongodump → encrypted tar.gz → B2 bucket. Trigger manual now or wait for auto.</div>
        </div>
        <button onClick={run} disabled={busy} className="btn-primary text-xs" data-testid="run-backup-btn">
          {busy ? 'Queuing…' : 'Run backup now'}
        </button>
      </div>
      {hist.length > 0 && (
        <div className="mt-2 max-h-40 overflow-y-auto" data-testid="backup-history">
          {hist.slice(0, 5).map(b => (
            <div key={b.backup_id} className="flex items-center justify-between text-xs py-1 border-b border-soft">
              <span className="font-mono">{b.backup_id}</span>
              <span className={`badge-${b.status==='completed' ? 'success' : b.status==='failed' ? 'error' : 'warn'}`}>{b.status}</span>
              <span className="text-fg-muted">{(b.queued_at || '').slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
        </div>
      )}
      {!process.env.B2_KEY_ID && (
        <div className="mt-2 text-[11px] text-fg-muted italic">
          To activate: set <code>B2_KEY_ID</code>, <code>B2_APPLICATION_KEY</code>, <code>B2_BUCKET_NAME</code> in <code>backend/.env</code> then restart backend.
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}



function StripeConnectSection() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ stripe_account_id: '', application_fee_percent: 0, accept_card: true, accept_apple_pay: true, accept_blik: false, accept_p24: false });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/clinic/me/payments').then(r => {
    setData(r.data);
    setForm({
      stripe_account_id: r.data.stripe_account_id || '',
      application_fee_percent: r.data.application_fee_percent || 0,
      accept_card: r.data.accept_card !== false,
      accept_apple_pay: r.data.accept_apple_pay !== false,
      accept_blik: r.data.accept_blik === true,
      accept_p24: r.data.accept_p24 === true,
    });
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/clinic/me/payments', form);
      setEdit(false);
      load();
    } catch (e) { alert('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!await safeConfirm('Disconnect Stripe? Future payments will fall back to the platform account.')) return;
    await api.post('/clinic/me/payments/disconnect');
    load();
  };

  const onboard = async () => {
    if (!await safeConfirm("This will open Stripe's secure onboarding to verify your account + add bank details. Continue?")) return;
    setSaving(true);
    try {
      const r = await api.post('/clinic/me/payments/onboard');
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        alert('No onboarding URL returned.');
      }
    } catch (e) {
      alert('Could not start onboarding: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const connected = !!data.stripe_account_id;
  const verified = data.stripe_charges_enabled && data.stripe_payouts_enabled;
  const onboardingAvailable = data.onboarding_available;

  return (
    <div data-testid="stripe-connect-card">
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-2 font-medium flex items-center gap-1.5">
        <Zap className="w-3.5 h-3.5 text-brand"/> Stripe Connect — card payouts
      </div>
      <p className="text-xs text-fg-muted mb-3">
        Connect your own Stripe account so card payments flow directly to <strong>your</strong> bank account.
        The platform takes the application fee you choose (set below).
      </p>

      {!connected && !edit && (
        <div className="surface border border-soft rounded-lg p-4 text-center">
          <Zap className="w-8 h-8 mx-auto text-fg-muted"/>
          <p className="text-sm text-fg mt-2"><strong>Not connected.</strong> Payments currently route to the platform default account.</p>
          <p className="text-xs text-fg-muted mt-1">
            One-click hosted onboarding lets you finish KYC + add your bank details on Stripe — typically under 3 minutes.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            {onboardingAvailable && (
              <button onClick={onboard} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="stripe-onboard-btn">
                <Zap className="w-4 h-4"/>
                {saving ? 'Opening Stripe…' : 'Connect Stripe (recommended)'}
              </button>
            )}
            <button onClick={() => setEdit(true)} className="btn-secondary text-sm" data-testid="stripe-connect-btn">
              Paste my account ID manually
            </button>
          </div>
        </div>
      )}

      {connected && verified === false && (
        <div className="mb-3 px-3 py-2 rounded-md bg-[#FFF4D6] border border-[#FFB000]/40 text-xs">
          <strong className="text-[#7A5500]">Onboarding incomplete</strong> — Stripe is waiting for KYC / bank details.
          {onboardingAvailable && (
            <button onClick={onboard} className="ml-2 text-brand underline" data-testid="stripe-resume-btn">Finish onboarding →</button>
          )}
        </div>
      )}

      {connected && !edit && (
        <div className="surface border border-green-200 bg-green-50 rounded-lg p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-green-700 font-semibold">Connected · {data.application_fee_percent}% platform fee</div>
              <div className="text-sm font-mono mt-1" data-testid="stripe-acct-display">{data.stripe_account_id}</div>
              <div className="text-[11px] text-fg-muted mt-1">
                Accepted: {[
                  data.accept_card && 'Card',
                  data.accept_apple_pay && 'Apple Pay',
                  data.accept_blik && 'BLIK',
                  data.accept_p24 && 'Przelewy24',
                ].filter(Boolean).join(' · ') || 'Nothing'}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEdit(true)} className="btn-secondary text-xs">Edit</button>
              <button onClick={disconnect} className="text-[#B83A3A] text-xs" data-testid="stripe-disconnect-btn">Disconnect</button>
            </div>
          </div>
        </div>
      )}

      {edit && (
        <div className="surface border border-soft rounded-lg p-4 space-y-3">
          <Field label="Stripe Connect account ID *">
            <input
              className="input font-mono"
              placeholder="acct_1Q12345abcDEF"
              value={form.stripe_account_id}
              onChange={e => setForm({ ...form, stripe_account_id: e.target.value })}
              data-testid="stripe-acct-input"
            />
          </Field>
          <Field label="Platform application fee % (0-30)">
            <input
              type="number" min="0" max="30" step="0.5"
              className="input"
              value={form.application_fee_percent}
              onChange={e => setForm({ ...form, application_fee_percent: parseFloat(e.target.value) || 0 })}
              data-testid="stripe-fee-input"
            />
          </Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {[
              ['accept_card', 'Card'],
              ['accept_apple_pay', 'Apple Pay'],
              ['accept_blik', 'BLIK (PL)'],
              ['accept_p24', 'Przelewy24 (PL)'],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 surface border border-soft rounded-md p-2 cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={e => setForm({ ...form, [k]: e.target.checked })} data-testid={`stripe-${k}`}/>
                <span className="text-xs">{l}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-soft">
            <button onClick={() => setEdit(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={save} disabled={saving || !form.stripe_account_id} className="btn-primary text-sm" data-testid="stripe-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
